import React, { useState, useEffect, useRef } from 'react';
import './PaymentModal.css';

const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

function getToken() {
  return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
}

/**
 * PaymentModal
 * Shows after user selects a paid plan.
 * Collects phone number → triggers STK Push → polls status every 3s.
 *
 * Props:
 *   isOpen       {boolean}
 *   plan         {object}  — { planId, planName, price, ... }
 *   adId         {number}  — the ad just created (pending activation)
 *   onSuccess    {fn}      — called when payment confirmed
 *   onClose      {fn}
 */
const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
  const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
  const [phone,     setPhone]     = useState('');
  const [error,     setError]     = useState('');
  const [loading,   setLoading]   = useState(false);
  const [receipt,   setReceipt]   = useState('');
  const [checkoutId, setCheckoutId] = useState('');
  const [pollCount,  setPollCount]  = useState(0);
  const pollRef = useRef(null);

  // Reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setStep('phone');
      setPhone('');
      setError('');
      setReceipt('');
      setCheckoutId('');
      setPollCount(0);
    }
    return () => clearInterval(pollRef.current);
  }, [isOpen]);

  if (!isOpen || !plan) return null;

  // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
  const handleInitiate = async () => {
    const cleaned = phone.trim().replace(/\s|-/g, '');
    if (!cleaned) { setError('Please enter your phone number.'); return; }
    if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
      setError('Enter a valid Kenyan number e.g. 0712345678'); return;
    }
    setError('');
    setLoading(true);

    try {
      const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
        method:  'POST',
        headers: {
          'Authorization': `Token ${getToken()}`,
          'Content-Type':  'application/json',
        },
        body: JSON.stringify({
          phone:  cleaned,
          plan:   plan.planId,
          ad_id:  adId,
          amount: amount,   // ✅ send the (possibly discounted) amount to backend
        }),
      });

      const data = await res.json();

      if (!res.ok) {
        setError(data.error || 'Failed to initiate payment. Try again.');
        setLoading(false);
        return;
      }

      setCheckoutId(data.checkout_request_id);
      setStep('waiting');
      setLoading(false);

      // Start polling every 3 seconds
      startPolling(data.checkout_request_id);

    } catch (e) {
      setError('Network error. Check your connection and try again.');
      setLoading(false);
    }
  };

  // ── Step 2: Poll payment status ───────────────────────────────────────────
  const startPolling = (cid) => {
    clearInterval(pollRef.current);
    setPollCount(0);

    pollRef.current = setInterval(async () => {
      setPollCount(c => {
        if (c >= 40) {           // 40 × 3s = 2 minutes timeout
          clearInterval(pollRef.current);
          setStep('failed');
          setError('Payment timed out. Please try again.');
          return c;
        }
        return c + 1;
      });

      try {
        const res = await fetch(
          `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
          { headers: { 'Authorization': `Token ${getToken()}` } }
        );
        const data = await res.json();

        if (data.status === 'completed') {
          clearInterval(pollRef.current);
          setReceipt(data.mpesa_receipt || '');
          setStep('success');
          // Notify parent after short delay so user sees success screen
          setTimeout(() => onSuccess?.(data), 2000);
        } else if (data.status === 'failed') {
          clearInterval(pollRef.current);
          setStep('failed');
          setError(data.result_desc || 'Payment was cancelled or failed.');
        }
        // 'pending' → keep polling
      } catch { /* ignore poll errors */ }
    }, 3000);
  };

  // ── Amount display ─────────────────────────────────────────────────────────
  // ✅ FIX: use plan.price first — this now carries the discounted price when a
  //    promotion is active.  The hardcoded map was always matching plan.planId
  //    first and silently ignoring the discounted price passed from PostAdModal.
  const amountFallback = { basic: 50, pro: 150, business: 500 };
  const amount = (plan?.price != null && plan.price > 0)
    ? plan.price
    : (amountFallback[plan?.planId] ?? 0);

  return (
    <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="pm-modal">

        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <span className="pm-logo">M-PESA</span>
            <span className="pm-plan-badge">{plan.planName} Plan</span>
          </div>
          {step === 'phone' && (
            <button className="pm-close" onClick={onClose}>✕</button>
          )}
        </div>

        {/* ── STEP: Phone input ─────────────────────────────────────────── */}
        {step === 'phone' && (
          <div className="pm-body">
            <div className="pm-amount-display">
              <span className="pm-currency">KES</span>
              <span className="pm-amount">{amount.toLocaleString()}</span>
            </div>
            <p className="pm-description">
              You will receive an M-Pesa prompt on your phone to complete this payment.
            </p>

            <label className="pm-label">
              M-Pesa Phone Number
              <div className="pm-phone-wrap">
                <span className="pm-phone-prefix">🇰🇪 +254</span>
                <input
                  className="pm-phone-input"
                  type="tel"
                  placeholder="712 345 678"
                  value={phone}
                  onChange={e => { setPhone(e.target.value); setError(''); }}
                  onKeyDown={e => e.key === 'Enter' && handleInitiate()}
                  autoFocus
                />
              </div>
              {error && <span className="pm-error">{error}</span>}
            </label>

            {/* Sandbox notice removed */}
            {/* <div className="pm-sandbox-notice">
              🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
            </div> */}

            <button
              className="pm-pay-btn"
              onClick={handleInitiate}
              disabled={loading}
            >
              {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
            </button>

            <button className="pm-back-btn" onClick={onClose}>
              ← Change Plan
            </button>

            {/* Bypass Payment button — dev/testing only (commented out for production)
            <button
              className="pm-bypass-btn"
              onClick={async () => {
                try {
                  const res = await fetch(`${API_BASE}/mpesa/bypass/`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      'Authorization': `Token ${getToken()}`,
                    },
                    body: JSON.stringify({
                      ad_id:  adId,
                      plan:   plan?.planId,
                      amount: amount,
                    }),
                  });
                  const data = await res.json();
                  if (res.ok && data.success) {
                    onSuccess?.({
                      status:        'completed',
                      mpesa_receipt: data.mpesa_receipt,
                      bypass:        true,
                    });
                  } else {
                    console.error('Bypass payment failed:', data);
                    alert(data.error || 'Bypass payment failed. Check console.');
                  }
                } catch (err) {
                  console.error('Bypass payment error:', err);
                  alert('Network error on bypass. Check console.');
                }
              }}
            >
              🚀 Bypass Payment (Dev Only)
            </button>
            */}
          </div>
        )}

        {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
        {step === 'waiting' && (
          <div className="pm-body pm-center">
            <div className="pm-spinner" />
            <h3 className="pm-waiting-title">Check Your Phone</h3>
            <p className="pm-waiting-desc">
              An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
              Enter your <strong>M-Pesa PIN</strong> to complete the payment.
            </p>
            <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
            <p className="pm-polling-hint">
              Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
            </p>
            <button
              className="pm-back-btn"
              onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
            >
              ← Cancel & Re-enter Number
            </button>
          </div>
        )}

        {/* ── STEP: Success ─────────────────────────────────────────────── */}
        {step === 'success' && (
          <div className="pm-body pm-center">
            <div className="pm-success-icon">✅</div>
            <h3 className="pm-success-title">Payment Confirmed!</h3>
            <p className="pm-success-desc">
              Your <strong>{plan.planName}</strong> plan is now active.
            </p>
            {receipt && (
              <div className="pm-receipt">
                <span className="pm-receipt-label">M-Pesa Receipt</span>
                <span className="pm-receipt-code">{receipt}</span>
              </div>
            )}
            <p className="pm-success-hint">Redirecting you to post your ad…</p>
          </div>
        )}

        {/* ── STEP: Failed ──────────────────────────────────────────────── */}
        {step === 'failed' && (
          <div className="pm-body pm-center">
            <div className="pm-fail-icon">❌</div>
            <h3 className="pm-fail-title">Payment Failed</h3>
            <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
            <button
              className="pm-pay-btn"
              onClick={() => { setStep('phone'); setError(''); }}
            >
              Try Again
            </button>
            <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
          </div>
        )}

      </div>
    </div>
  );
};

export default PaymentModal;


































// import React, { useState, useEffect, useRef } from 'react';
// import './PaymentModal.css';

// const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// function getToken() {
//   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// }

// /**
//  * PaymentModal
//  * Shows after user selects a paid plan.
//  * Collects phone number → triggers STK Push → polls status every 3s.
//  *
//  * Props:
//  *   isOpen       {boolean}
//  *   plan         {object}  — { planId, planName, price, ... }
//  *   adId         {number}  — the ad just created (pending activation)
//  *   onSuccess    {fn}      — called when payment confirmed
//  *   onClose      {fn}
//  */
// const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
//   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
//   const [phone,     setPhone]     = useState('');
//   const [error,     setError]     = useState('');
//   const [loading,   setLoading]   = useState(false);
//   const [receipt,   setReceipt]   = useState('');
//   const [checkoutId, setCheckoutId] = useState('');
//   const [pollCount,  setPollCount]  = useState(0);
//   const pollRef = useRef(null);

//   // Reset when modal opens
//   useEffect(() => {
//     if (isOpen) {
//       setStep('phone');
//       setPhone('');
//       setError('');
//       setReceipt('');
//       setCheckoutId('');
//       setPollCount(0);
//     }
//     return () => clearInterval(pollRef.current);
//   }, [isOpen]);

//   if (!isOpen || !plan) return null;

//   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
//   const handleInitiate = async () => {
//     const cleaned = phone.trim().replace(/\s|-/g, '');
//     if (!cleaned) { setError('Please enter your phone number.'); return; }
//     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
//       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
//     }
//     setError('');
//     setLoading(true);

//     try {
//       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
//         method:  'POST',
//         headers: {
//           'Authorization': `Token ${getToken()}`,
//           'Content-Type':  'application/json',
//         },
//         body: JSON.stringify({
//           phone:  cleaned,
//           plan:   plan.planId,
//           ad_id:  adId,
//           amount: amount,   // ✅ send the (possibly discounted) amount to backend
//         }),
//       });

//       const data = await res.json();

//       if (!res.ok) {
//         setError(data.error || 'Failed to initiate payment. Try again.');
//         setLoading(false);
//         return;
//       }

//       setCheckoutId(data.checkout_request_id);
//       setStep('waiting');
//       setLoading(false);

//       // Start polling every 3 seconds
//       startPolling(data.checkout_request_id);

//     } catch (e) {
//       setError('Network error. Check your connection and try again.');
//       setLoading(false);
//     }
//   };

//   // ── Step 2: Poll payment status ───────────────────────────────────────────
//   const startPolling = (cid) => {
//     clearInterval(pollRef.current);
//     setPollCount(0);

//     pollRef.current = setInterval(async () => {
//       setPollCount(c => {
//         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
//           clearInterval(pollRef.current);
//           setStep('failed');
//           setError('Payment timed out. Please try again.');
//           return c;
//         }
//         return c + 1;
//       });

//       try {
//         const res = await fetch(
//           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
//           { headers: { 'Authorization': `Token ${getToken()}` } }
//         );
//         const data = await res.json();

//         if (data.status === 'completed') {
//           clearInterval(pollRef.current);
//           setReceipt(data.mpesa_receipt || '');
//           setStep('success');
//           // Notify parent after short delay so user sees success screen
//           setTimeout(() => onSuccess?.(data), 2000);
//         } else if (data.status === 'failed') {
//           clearInterval(pollRef.current);
//           setStep('failed');
//           setError(data.result_desc || 'Payment was cancelled or failed.');
//         }
//         // 'pending' → keep polling
//       } catch { /* ignore poll errors */ }
//     }, 3000);
//   };

//   // ── Amount display ─────────────────────────────────────────────────────────
//   // ✅ FIX: use plan.price first — this now carries the discounted price when a
//   //    promotion is active.  The hardcoded map was always matching plan.planId
//   //    first and silently ignoring the discounted price passed from PostAdModal.
//   const amountFallback = { basic: 50, pro: 150, business: 500 };
//   const amount = (plan?.price != null && plan.price > 0)
//     ? plan.price
//     : (amountFallback[plan?.planId] ?? 0);

//   return (
//     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
//       <div className="pm-modal">

//         {/* Header */}
//         <div className="pm-header">
//           <div className="pm-header-left">
//             <span className="pm-logo">M-PESA</span>
//             <span className="pm-plan-badge">{plan.planName} Plan</span>
//           </div>
//           {step === 'phone' && (
//             <button className="pm-close" onClick={onClose}>✕</button>
//           )}
//         </div>

//         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
//         {step === 'phone' && (
//           <div className="pm-body">
//             <div className="pm-amount-display">
//               <span className="pm-currency">KES</span>
//               <span className="pm-amount">{amount.toLocaleString()}</span>
//             </div>
//             <p className="pm-description">
//               You will receive an M-Pesa prompt on your phone to complete this payment.
//             </p>

//             <label className="pm-label">
//               M-Pesa Phone Number
//               <div className="pm-phone-wrap">
//                 <span className="pm-phone-prefix">🇰🇪 +254</span>
//                 <input
//                   className="pm-phone-input"
//                   type="tel"
//                   placeholder="712 345 678"
//                   value={phone}
//                   onChange={e => { setPhone(e.target.value); setError(''); }}
//                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
//                   autoFocus
//                 />
//               </div>
//               {error && <span className="pm-error">{error}</span>}
//             </label>

//             <div className="pm-sandbox-notice">
//               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
//             </div>

//             <button
//               className="pm-pay-btn"
//               onClick={handleInitiate}
//               disabled={loading}
//             >
//               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
//             </button>

//             <button className="pm-back-btn" onClick={onClose}>
//               ← Change Plan
//             </button>

//             {/* ✅ Bypass Payment button — dev/testing only.
//                 Calls /api/mpesa/bypass/ which creates a real MpesaTransaction,
//                 activates the AdPlan, AND fires record_commission() so referral
//                 commissions are correctly wired — same as the real M-Pesa callback. */}
//             <button
//               className="pm-bypass-btn"
//               onClick={async () => {
//                 try {
//                   const res = await fetch(`${API_BASE}/mpesa/bypass/`, {
//                     method: 'POST',
//                     headers: {
//                       'Content-Type': 'application/json',
//                       'Authorization': `Token ${getToken()}`,
//                     },
//                     body: JSON.stringify({
//                       ad_id:  adId,
//                       plan:   plan?.planId,
//                       amount: amount,
//                     }),
//                   });
//                   const data = await res.json();
//                   if (res.ok && data.success) {
//                     onSuccess?.({
//                       status:        'completed',
//                       mpesa_receipt: data.mpesa_receipt,
//                       bypass:        true,
//                     });
//                   } else {
//                     console.error('Bypass payment failed:', data);
//                     alert(data.error || 'Bypass payment failed. Check console.');
//                   }
//                 } catch (err) {
//                   console.error('Bypass payment error:', err);
//                   alert('Network error on bypass. Check console.');
//                 }
//               }}
//             >
//               🚀 Bypass Payment (Dev Only)
//             </button>
//           </div>
//         )}

//         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
//         {step === 'waiting' && (
//           <div className="pm-body pm-center">
//             <div className="pm-spinner" />
//             <h3 className="pm-waiting-title">Check Your Phone</h3>
//             <p className="pm-waiting-desc">
//               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
//               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
//             </p>
//             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
//             <p className="pm-polling-hint">
//               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
//             </p>
//             <button
//               className="pm-back-btn"
//               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
//             >
//               ← Cancel & Re-enter Number
//             </button>
//           </div>
//         )}

//         {/* ── STEP: Success ─────────────────────────────────────────────── */}
//         {step === 'success' && (
//           <div className="pm-body pm-center">
//             <div className="pm-success-icon">✅</div>
//             <h3 className="pm-success-title">Payment Confirmed!</h3>
//             <p className="pm-success-desc">
//               Your <strong>{plan.planName}</strong> plan is now active.
//             </p>
//             {receipt && (
//               <div className="pm-receipt">
//                 <span className="pm-receipt-label">M-Pesa Receipt</span>
//                 <span className="pm-receipt-code">{receipt}</span>
//               </div>
//             )}
//             <p className="pm-success-hint">Redirecting you to post your ad…</p>
//           </div>
//         )}

//         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
//         {step === 'failed' && (
//           <div className="pm-body pm-center">
//             <div className="pm-fail-icon">❌</div>
//             <h3 className="pm-fail-title">Payment Failed</h3>
//             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
//             <button
//               className="pm-pay-btn"
//               onClick={() => { setStep('phone'); setError(''); }}
//             >
//               Try Again
//             </button>
//             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
//           </div>
//         )}

//       </div>
//     </div>
//   );
// };

// export default PaymentModal;













































// // import React, { useState, useEffect, useRef } from 'react';
// // import './PaymentModal.css';

// // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // function getToken() {
// //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // }

// // /**
// //  * PaymentModal
// //  * Shows after user selects a paid plan.
// //  * Collects phone number → triggers STK Push → polls status every 3s.
// //  *
// //  * Props:
// //  *   isOpen       {boolean}
// //  *   plan         {object}  — { planId, planName, price, ... }
// //  *   adId         {number}  — the ad just created (pending activation)
// //  *   onSuccess    {fn}      — called when payment confirmed
// //  *   onClose      {fn}
// //  */
// // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// //   const [phone,     setPhone]     = useState('');
// //   const [error,     setError]     = useState('');
// //   const [loading,   setLoading]   = useState(false);
// //   const [receipt,   setReceipt]   = useState('');
// //   const [checkoutId, setCheckoutId] = useState('');
// //   const [pollCount,  setPollCount]  = useState(0);
// //   const pollRef = useRef(null);

// //   // Reset when modal opens
// //   useEffect(() => {
// //     if (isOpen) {
// //       setStep('phone');
// //       setPhone('');
// //       setError('');
// //       setReceipt('');
// //       setCheckoutId('');
// //       setPollCount(0);
// //     }
// //     return () => clearInterval(pollRef.current);
// //   }, [isOpen]);

// //   if (!isOpen || !plan) return null;

// //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// //   const handleInitiate = async () => {
// //     const cleaned = phone.trim().replace(/\s|-/g, '');
// //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// //     }
// //     setError('');
// //     setLoading(true);

// //     try {
// //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// //         method:  'POST',
// //         headers: {
// //           'Authorization': `Token ${getToken()}`,
// //           'Content-Type':  'application/json',
// //         },
// //         body: JSON.stringify({
// //           phone:  cleaned,
// //           plan:   plan.planId,
// //           ad_id:  adId,
// //           amount: amount,   // ✅ send the (possibly discounted) amount to backend
// //         }),
// //       });

// //       const data = await res.json();

// //       if (!res.ok) {
// //         setError(data.error || 'Failed to initiate payment. Try again.');
// //         setLoading(false);
// //         return;
// //       }

// //       setCheckoutId(data.checkout_request_id);
// //       setStep('waiting');
// //       setLoading(false);

// //       // Start polling every 3 seconds
// //       startPolling(data.checkout_request_id);

// //     } catch (e) {
// //       setError('Network error. Check your connection and try again.');
// //       setLoading(false);
// //     }
// //   };

// //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// //   const startPolling = (cid) => {
// //     clearInterval(pollRef.current);
// //     setPollCount(0);

// //     pollRef.current = setInterval(async () => {
// //       setPollCount(c => {
// //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// //           clearInterval(pollRef.current);
// //           setStep('failed');
// //           setError('Payment timed out. Please try again.');
// //           return c;
// //         }
// //         return c + 1;
// //       });

// //       try {
// //         const res = await fetch(
// //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// //           { headers: { 'Authorization': `Token ${getToken()}` } }
// //         );
// //         const data = await res.json();

// //         if (data.status === 'completed') {
// //           clearInterval(pollRef.current);
// //           setReceipt(data.mpesa_receipt || '');
// //           setStep('success');
// //           // Notify parent after short delay so user sees success screen
// //           setTimeout(() => onSuccess?.(data), 2000);
// //         } else if (data.status === 'failed') {
// //           clearInterval(pollRef.current);
// //           setStep('failed');
// //           setError(data.result_desc || 'Payment was cancelled or failed.');
// //         }
// //         // 'pending' → keep polling
// //       } catch { /* ignore poll errors */ }
// //     }, 3000);
// //   };

// //   // ── Amount display ─────────────────────────────────────────────────────────
// //   // ✅ FIX: use plan.price first — this now carries the discounted price when a
// //   //    promotion is active.  The hardcoded map was always matching plan.planId
// //   //    first and silently ignoring the discounted price passed from PostAdModal.
// //   const amountFallback = { basic: 50, pro: 150, business: 500 };
// //   const amount = (plan?.price != null && plan.price > 0)
// //     ? plan.price
// //     : (amountFallback[plan?.planId] ?? 0);

// //   return (
// //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// //       <div className="pm-modal">

// //         {/* Header */}
// //         <div className="pm-header">
// //           <div className="pm-header-left">
// //             <span className="pm-logo">M-PESA</span>
// //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// //           </div>
// //           {step === 'phone' && (
// //             <button className="pm-close" onClick={onClose}>✕</button>
// //           )}
// //         </div>

// //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// //         {step === 'phone' && (
// //           <div className="pm-body">
// //             <div className="pm-amount-display">
// //               <span className="pm-currency">KES</span>
// //               <span className="pm-amount">{amount.toLocaleString()}</span>
// //             </div>
// //             <p className="pm-description">
// //               You will receive an M-Pesa prompt on your phone to complete this payment.
// //             </p>

// //             <label className="pm-label">
// //               M-Pesa Phone Number
// //               <div className="pm-phone-wrap">
// //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// //                 <input
// //                   className="pm-phone-input"
// //                   type="tel"
// //                   placeholder="712 345 678"
// //                   value={phone}
// //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// //                   autoFocus
// //                 />
// //               </div>
// //               {error && <span className="pm-error">{error}</span>}
// //             </label>

// //             <div className="pm-sandbox-notice">
// //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// //             </div>

// //             <button
// //               className="pm-pay-btn"
// //               onClick={handleInitiate}
// //               disabled={loading}
// //             >
// //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// //             </button>

// //             <button className="pm-back-btn" onClick={onClose}>
// //               ← Change Plan
// //             </button>

// //             {/* ✅ NEW: Bypass Payment button (temporary for testing) */}
// //             <button 
// //               className="pm-bypass-btn"
// //               onClick={() => {
// //                 // Call onSuccess without actual payment
// //                 // This simulates a successful payment
// //                 onSuccess?.({
// //                   status: 'completed',
// //                   mpesa_receipt: 'BYPASS-' + Date.now(),
// //                   bypass: true,
// //                 });
// //               }}
// //             >
// //               🚀 Bypass Payment (Dev Only)
// //             </button>
// //           </div>
// //         )}

// //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// //         {step === 'waiting' && (
// //           <div className="pm-body pm-center">
// //             <div className="pm-spinner" />
// //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// //             <p className="pm-waiting-desc">
// //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// //             </p>
// //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// //             <p className="pm-polling-hint">
// //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// //             </p>
// //             <button
// //               className="pm-back-btn"
// //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// //             >
// //               ← Cancel & Re-enter Number
// //             </button>
// //           </div>
// //         )}

// //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// //         {step === 'success' && (
// //           <div className="pm-body pm-center">
// //             <div className="pm-success-icon">✅</div>
// //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// //             <p className="pm-success-desc">
// //               Your <strong>{plan.planName}</strong> plan is now active.
// //             </p>
// //             {receipt && (
// //               <div className="pm-receipt">
// //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// //                 <span className="pm-receipt-code">{receipt}</span>
// //               </div>
// //             )}
// //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// //           </div>
// //         )}

// //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// //         {step === 'failed' && (
// //           <div className="pm-body pm-center">
// //             <div className="pm-fail-icon">❌</div>
// //             <h3 className="pm-fail-title">Payment Failed</h3>
// //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// //             <button
// //               className="pm-pay-btn"
// //               onClick={() => { setStep('phone'); setError(''); }}
// //             >
// //               Try Again
// //             </button>
// //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// //           </div>
// //         )}

// //       </div>
// //     </div>
// //   );
// // };

// // export default PaymentModal;





























// // // import React, { useState, useEffect, useRef } from 'react';
// // // import './PaymentModal.css';

// // // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // function getToken() {
// // //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // // }

// // // /**
// // //  * PaymentModal
// // //  * Shows after user selects a paid plan.
// // //  * Collects phone number → triggers STK Push → polls status every 3s.
// // //  *
// // //  * Props:
// // //  *   isOpen       {boolean}
// // //  *   plan         {object}  — { planId, planName, price, ... }
// // //  *   adId         {number}  — the ad just created (pending activation)
// // //  *   onSuccess    {fn}      — called when payment confirmed
// // //  *   onClose      {fn}
// // //  */
// // // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// // //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// // //   const [phone,     setPhone]     = useState('');
// // //   const [error,     setError]     = useState('');
// // //   const [loading,   setLoading]   = useState(false);
// // //   const [receipt,   setReceipt]   = useState('');
// // //   const [checkoutId, setCheckoutId] = useState('');
// // //   const [pollCount,  setPollCount]  = useState(0);
// // //   const pollRef = useRef(null);

// // //   // Reset when modal opens
// // //   useEffect(() => {
// // //     if (isOpen) {
// // //       setStep('phone');
// // //       setPhone('');
// // //       setError('');
// // //       setReceipt('');
// // //       setCheckoutId('');
// // //       setPollCount(0);
// // //     }
// // //     return () => clearInterval(pollRef.current);
// // //   }, [isOpen]);

// // //   if (!isOpen || !plan) return null;

// // //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// // //   const handleInitiate = async () => {
// // //     const cleaned = phone.trim().replace(/\s|-/g, '');
// // //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// // //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// // //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// // //     }
// // //     setError('');
// // //     setLoading(true);

// // //     try {
// // //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// // //         method:  'POST',
// // //         headers: {
// // //           'Authorization': `Token ${getToken()}`,
// // //           'Content-Type':  'application/json',
// // //         },
// // //         body: JSON.stringify({
// // //           phone:  cleaned,
// // //           plan:   plan.planId,
// // //           ad_id:  adId,
// // //         }),
// // //       });

// // //       const data = await res.json();

// // //       if (!res.ok) {
// // //         setError(data.error || 'Failed to initiate payment. Try again.');
// // //         setLoading(false);
// // //         return;
// // //       }

// // //       setCheckoutId(data.checkout_request_id);
// // //       setStep('waiting');
// // //       setLoading(false);

// // //       // Start polling every 3 seconds
// // //       startPolling(data.checkout_request_id);

// // //     } catch (e) {
// // //       setError('Network error. Check your connection and try again.');
// // //       setLoading(false);
// // //     }
// // //   };

// // //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// // //   const startPolling = (cid) => {
// // //     clearInterval(pollRef.current);
// // //     setPollCount(0);

// // //     pollRef.current = setInterval(async () => {
// // //       setPollCount(c => {
// // //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// // //           clearInterval(pollRef.current);
// // //           setStep('failed');
// // //           setError('Payment timed out. Please try again.');
// // //           return c;
// // //         }
// // //         return c + 1;
// // //       });

// // //       try {
// // //         const res = await fetch(
// // //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// // //           { headers: { 'Authorization': `Token ${getToken()}` } }
// // //         );
// // //         const data = await res.json();

// // //         if (data.status === 'completed') {
// // //           clearInterval(pollRef.current);
// // //           setReceipt(data.mpesa_receipt || '');
// // //           setStep('success');
// // //           // Notify parent after short delay so user sees success screen
// // //           setTimeout(() => onSuccess?.(data), 2000);
// // //         } else if (data.status === 'failed') {
// // //           clearInterval(pollRef.current);
// // //           setStep('failed');
// // //           setError(data.result_desc || 'Payment was cancelled or failed.');
// // //         }
// // //         // 'pending' → keep polling
// // //       } catch { /* ignore poll errors */ }
// // //     }, 3000);
// // //   };

// // //   // ── Amount display ────────────────────────────────────────────────────────
// // //   const amountMap = { basic: 50, pro: 150, business: 500 };
// // //   const amount    = amountMap[plan?.planId] || plan?.price || 0;

// // //   return (
// // //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// // //       <div className="pm-modal">

// // //         {/* Header */}
// // //         <div className="pm-header">
// // //           <div className="pm-header-left">
// // //             <span className="pm-logo">M-PESA</span>
// // //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// // //           </div>
// // //           {step === 'phone' && (
// // //             <button className="pm-close" onClick={onClose}>✕</button>
// // //           )}
// // //         </div>

// // //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// // //         {step === 'phone' && (
// // //           <div className="pm-body">
// // //             <div className="pm-amount-display">
// // //               <span className="pm-currency">KES</span>
// // //               <span className="pm-amount">{amount.toLocaleString()}</span>
// // //             </div>
// // //             <p className="pm-description">
// // //               You will receive an M-Pesa prompt on your phone to complete this payment.
// // //             </p>

// // //             <label className="pm-label">
// // //               M-Pesa Phone Number
// // //               <div className="pm-phone-wrap">
// // //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// // //                 <input
// // //                   className="pm-phone-input"
// // //                   type="tel"
// // //                   placeholder="712 345 678"
// // //                   value={phone}
// // //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// // //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// // //                   autoFocus
// // //                 />
// // //               </div>
// // //               {error && <span className="pm-error">{error}</span>}
// // //             </label>

// // //             <div className="pm-sandbox-notice">
// // //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// // //             </div>

// // //             <button
// // //               className="pm-pay-btn"
// // //               onClick={handleInitiate}
// // //               disabled={loading}
// // //             >
// // //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// // //             </button>

// // //             <button className="pm-back-btn" onClick={onClose}>
// // //               ← Change Plan
// // //             </button>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// // //         {step === 'waiting' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-spinner" />
// // //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// // //             <p className="pm-waiting-desc">
// // //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// // //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// // //             </p>
// // //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// // //             <p className="pm-polling-hint">
// // //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// // //             </p>
// // //             <button
// // //               className="pm-back-btn"
// // //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// // //             >
// // //               ← Cancel & Re-enter Number
// // //             </button>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// // //         {step === 'success' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-success-icon">✅</div>
// // //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// // //             <p className="pm-success-desc">
// // //               Your <strong>{plan.planName}</strong> plan is now active.
// // //             </p>
// // //             {receipt && (
// // //               <div className="pm-receipt">
// // //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// // //                 <span className="pm-receipt-code">{receipt}</span>
// // //               </div>
// // //             )}
// // //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// // //         {step === 'failed' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-fail-icon">❌</div>
// // //             <h3 className="pm-fail-title">Payment Failed</h3>
// // //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// // //             <button
// // //               className="pm-pay-btn"
// // //               onClick={() => { setStep('phone'); setError(''); }}
// // //             >
// // //               Try Again
// // //             </button>
// // //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// // //           </div>
// // //         )}

// // //       </div>
// // //     </div>
// // //   );
// // // };

// // // export default PaymentModal;




























// // // import React, { useState, useEffect, useRef } from 'react';
// // // import './PaymentModal.css';

// // // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // function getToken() {
// // //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // // }

// // // /**
// // //  * PaymentModal
// // //  * Shows after user selects a paid plan.
// // //  * Collects phone number → triggers STK Push → polls status every 3s.
// // //  *
// // //  * Props:
// // //  *   isOpen       {boolean}
// // //  *   plan         {object}  — { planId, planName, price, ... }
// // //  *   adId         {number}  — the ad just created (pending activation)
// // //  *   onSuccess    {fn}      — called when payment confirmed
// // //  *   onClose      {fn}
// // //  */
// // // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// // //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// // //   const [phone,     setPhone]     = useState('');
// // //   const [error,     setError]     = useState('');
// // //   const [loading,   setLoading]   = useState(false);
// // //   const [receipt,   setReceipt]   = useState('');
// // //   const [checkoutId, setCheckoutId] = useState('');
// // //   const [pollCount,  setPollCount]  = useState(0);
// // //   const pollRef = useRef(null);

// // //   // Reset when modal opens
// // //   useEffect(() => {
// // //     if (isOpen) {
// // //       setStep('phone');
// // //       setPhone('');
// // //       setError('');
// // //       setReceipt('');
// // //       setCheckoutId('');
// // //       setPollCount(0);
// // //     }
// // //     return () => clearInterval(pollRef.current);
// // //   }, [isOpen]);

// // //   if (!isOpen || !plan) return null;

// // //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// // //   const handleInitiate = async () => {
// // //     const cleaned = phone.trim().replace(/\s|-/g, '');
// // //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// // //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// // //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// // //     }
// // //     setError('');
// // //     setLoading(true);

// // //     try {
// // //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// // //         method:  'POST',
// // //         headers: {
// // //           'Authorization': `Token ${getToken()}`,
// // //           'Content-Type':  'application/json',
// // //         },
// // //         body: JSON.stringify({
// // //           phone:  cleaned,
// // //           plan:   plan.planId,
// // //           ad_id:  adId,
// // //         }),
// // //       });

// // //       const data = await res.json();

// // //       if (!res.ok) {
// // //         setError(data.error || 'Failed to initiate payment. Try again.');
// // //         setLoading(false);
// // //         return;
// // //       }

// // //       setCheckoutId(data.checkout_request_id);
// // //       setStep('waiting');
// // //       setLoading(false);

// // //       // Start polling every 3 seconds
// // //       startPolling(data.checkout_request_id);

// // //     } catch (e) {
// // //       setError('Network error. Check your connection and try again.');
// // //       setLoading(false);
// // //     }
// // //   };

// // //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// // //   const startPolling = (cid) => {
// // //     clearInterval(pollRef.current);
// // //     setPollCount(0);

// // //     pollRef.current = setInterval(async () => {
// // //       setPollCount(c => {
// // //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// // //           clearInterval(pollRef.current);
// // //           setStep('failed');
// // //           setError('Payment timed out. Please try again.');
// // //           return c;
// // //         }
// // //         return c + 1;
// // //       });

// // //       try {
// // //         const res = await fetch(
// // //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// // //           { headers: { 'Authorization': `Token ${getToken()}` } }
// // //         );
// // //         const data = await res.json();

// // //         if (data.status === 'completed') {
// // //           clearInterval(pollRef.current);
// // //           setReceipt(data.mpesa_receipt || '');
// // //           setStep('success');
// // //           // Notify parent after short delay so user sees success screen
// // //           setTimeout(() => onSuccess?.(data), 2000);
// // //         } else if (data.status === 'failed') {
// // //           clearInterval(pollRef.current);
// // //           setStep('failed');
// // //           setError(data.result_desc || 'Payment was cancelled or failed.');
// // //         }
// // //         // 'pending' → keep polling
// // //       } catch { /* ignore poll errors */ }
// // //     }, 3000);
// // //   };

// // //   // ── Amount display ─────────────────────────────────────────────────────────
// // //   // ✅ FIX: use plan.price first — this now carries the discounted price when a
// // //   //    promotion is active.  The hardcoded map was always matching plan.planId
// // //   //    first and silently ignoring the discounted price passed from PostAdModal.
// // //   const amountFallback = { basic: 50, pro: 150, business: 500 };
// // //   const amount = (plan?.price != null && plan.price > 0)
// // //     ? plan.price
// // //     : (amountFallback[plan?.planId] ?? 0);

// // //   return (
// // //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// // //       <div className="pm-modal">

// // //         {/* Header */}
// // //         <div className="pm-header">
// // //           <div className="pm-header-left">
// // //             <span className="pm-logo">M-PESA</span>
// // //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// // //           </div>
// // //           {step === 'phone' && (
// // //             <button className="pm-close" onClick={onClose}>✕</button>
// // //           )}
// // //         </div>

// // //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// // //         {step === 'phone' && (
// // //           <div className="pm-body">
// // //             <div className="pm-amount-display">
// // //               <span className="pm-currency">KES</span>
// // //               <span className="pm-amount">{amount.toLocaleString()}</span>
// // //             </div>
// // //             <p className="pm-description">
// // //               You will receive an M-Pesa prompt on your phone to complete this payment.
// // //             </p>

// // //             <label className="pm-label">
// // //               M-Pesa Phone Number
// // //               <div className="pm-phone-wrap">
// // //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// // //                 <input
// // //                   className="pm-phone-input"
// // //                   type="tel"
// // //                   placeholder="712 345 678"
// // //                   value={phone}
// // //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// // //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// // //                   autoFocus
// // //                 />
// // //               </div>
// // //               {error && <span className="pm-error">{error}</span>}
// // //             </label>

// // //             <div className="pm-sandbox-notice">
// // //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// // //             </div>

// // //             <button
// // //               className="pm-pay-btn"
// // //               onClick={handleInitiate}
// // //               disabled={loading}
// // //             >
// // //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// // //             </button>

// // //             <button className="pm-back-btn" onClick={onClose}>
// // //               ← Change Plan
// // //             </button>

// // //             {/* ✅ NEW: Bypass Payment button (temporary for testing) */}
// // //             <button 
// // //               className="pm-bypass-btn"
// // //               onClick={() => {
// // //                 // Call onSuccess without actual payment
// // //                 // This simulates a successful payment
// // //                 onSuccess?.({
// // //                   status: 'completed',
// // //                   mpesa_receipt: 'BYPASS-' + Date.now(),
// // //                   bypass: true,
// // //                 });
// // //               }}
// // //             >
// // //               🚀 Bypass Payment (Dev Only)
// // //             </button>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// // //         {step === 'waiting' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-spinner" />
// // //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// // //             <p className="pm-waiting-desc">
// // //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// // //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// // //             </p>
// // //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// // //             <p className="pm-polling-hint">
// // //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// // //             </p>
// // //             <button
// // //               className="pm-back-btn"
// // //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// // //             >
// // //               ← Cancel & Re-enter Number
// // //             </button>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// // //         {step === 'success' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-success-icon">✅</div>
// // //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// // //             <p className="pm-success-desc">
// // //               Your <strong>{plan.planName}</strong> plan is now active.
// // //             </p>
// // //             {receipt && (
// // //               <div className="pm-receipt">
// // //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// // //                 <span className="pm-receipt-code">{receipt}</span>
// // //               </div>
// // //             )}
// // //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// // //           </div>
// // //         )}

// // //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// // //         {step === 'failed' && (
// // //           <div className="pm-body pm-center">
// // //             <div className="pm-fail-icon">❌</div>
// // //             <h3 className="pm-fail-title">Payment Failed</h3>
// // //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// // //             <button
// // //               className="pm-pay-btn"
// // //               onClick={() => { setStep('phone'); setError(''); }}
// // //             >
// // //               Try Again
// // //             </button>
// // //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// // //           </div>
// // //         )}

// // //       </div>
// // //     </div>
// // //   );
// // // };

// // // export default PaymentModal;





























// // // // import React, { useState, useEffect, useRef } from 'react';
// // // // import './PaymentModal.css';

// // // // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // // function getToken() {
// // // //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // // // }

// // // // /**
// // // //  * PaymentModal
// // // //  * Shows after user selects a paid plan.
// // // //  * Collects phone number → triggers STK Push → polls status every 3s.
// // // //  *
// // // //  * Props:
// // // //  *   isOpen       {boolean}
// // // //  *   plan         {object}  — { planId, planName, price, ... }
// // // //  *   adId         {number}  — the ad just created (pending activation)
// // // //  *   onSuccess    {fn}      — called when payment confirmed
// // // //  *   onClose      {fn}
// // // //  */
// // // // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// // // //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// // // //   const [phone,     setPhone]     = useState('');
// // // //   const [error,     setError]     = useState('');
// // // //   const [loading,   setLoading]   = useState(false);
// // // //   const [receipt,   setReceipt]   = useState('');
// // // //   const [checkoutId, setCheckoutId] = useState('');
// // // //   const [pollCount,  setPollCount]  = useState(0);
// // // //   const pollRef = useRef(null);

// // // //   // Reset when modal opens
// // // //   useEffect(() => {
// // // //     if (isOpen) {
// // // //       setStep('phone');
// // // //       setPhone('');
// // // //       setError('');
// // // //       setReceipt('');
// // // //       setCheckoutId('');
// // // //       setPollCount(0);
// // // //     }
// // // //     return () => clearInterval(pollRef.current);
// // // //   }, [isOpen]);

// // // //   if (!isOpen || !plan) return null;

// // // //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// // // //   const handleInitiate = async () => {
// // // //     const cleaned = phone.trim().replace(/\s|-/g, '');
// // // //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// // // //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// // // //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// // // //     }
// // // //     setError('');
// // // //     setLoading(true);

// // // //     try {
// // // //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// // // //         method:  'POST',
// // // //         headers: {
// // // //           'Authorization': `Token ${getToken()}`,
// // // //           'Content-Type':  'application/json',
// // // //         },
// // // //         body: JSON.stringify({
// // // //           phone:  cleaned,
// // // //           plan:   plan.planId,
// // // //           ad_id:  adId,
// // // //         }),
// // // //       });

// // // //       const data = await res.json();

// // // //       if (!res.ok) {
// // // //         setError(data.error || 'Failed to initiate payment. Try again.');
// // // //         setLoading(false);
// // // //         return;
// // // //       }

// // // //       setCheckoutId(data.checkout_request_id);
// // // //       setStep('waiting');
// // // //       setLoading(false);

// // // //       // Start polling every 3 seconds
// // // //       startPolling(data.checkout_request_id);

// // // //     } catch (e) {
// // // //       setError('Network error. Check your connection and try again.');
// // // //       setLoading(false);
// // // //     }
// // // //   };

// // // //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// // // //   const startPolling = (cid) => {
// // // //     clearInterval(pollRef.current);
// // // //     setPollCount(0);

// // // //     pollRef.current = setInterval(async () => {
// // // //       setPollCount(c => {
// // // //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// // // //           clearInterval(pollRef.current);
// // // //           setStep('failed');
// // // //           setError('Payment timed out. Please try again.');
// // // //           return c;
// // // //         }
// // // //         return c + 1;
// // // //       });

// // // //       try {
// // // //         const res = await fetch(
// // // //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// // // //           { headers: { 'Authorization': `Token ${getToken()}` } }
// // // //         );
// // // //         const data = await res.json();

// // // //         if (data.status === 'completed') {
// // // //           clearInterval(pollRef.current);
// // // //           setReceipt(data.mpesa_receipt || '');
// // // //           setStep('success');
// // // //           // Notify parent after short delay so user sees success screen
// // // //           setTimeout(() => onSuccess?.(data), 2000);
// // // //         } else if (data.status === 'failed') {
// // // //           clearInterval(pollRef.current);
// // // //           setStep('failed');
// // // //           setError(data.result_desc || 'Payment was cancelled or failed.');
// // // //         }
// // // //         // 'pending' → keep polling
// // // //       } catch { /* ignore poll errors */ }
// // // //     }, 3000);
// // // //   };

// // // //   // ── Amount display ────────────────────────────────────────────────────────
// // // //   const amountMap = { basic: 50, pro: 150, business: 500 };
// // // //   const amount    = amountMap[plan?.planId] || plan?.price || 0;

// // // //   return (
// // // //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// // // //       <div className="pm-modal">

// // // //         {/* Header */}
// // // //         <div className="pm-header">
// // // //           <div className="pm-header-left">
// // // //             <span className="pm-logo">M-PESA</span>
// // // //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// // // //           </div>
// // // //           {step === 'phone' && (
// // // //             <button className="pm-close" onClick={onClose}>✕</button>
// // // //           )}
// // // //         </div>

// // // //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// // // //         {step === 'phone' && (
// // // //           <div className="pm-body">
// // // //             <div className="pm-amount-display">
// // // //               <span className="pm-currency">KES</span>
// // // //               <span className="pm-amount">{amount.toLocaleString()}</span>
// // // //             </div>
// // // //             <p className="pm-description">
// // // //               You will receive an M-Pesa prompt on your phone to complete this payment.
// // // //             </p>

// // // //             <label className="pm-label">
// // // //               M-Pesa Phone Number
// // // //               <div className="pm-phone-wrap">
// // // //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// // // //                 <input
// // // //                   className="pm-phone-input"
// // // //                   type="tel"
// // // //                   placeholder="712 345 678"
// // // //                   value={phone}
// // // //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// // // //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// // // //                   autoFocus
// // // //                 />
// // // //               </div>
// // // //               {error && <span className="pm-error">{error}</span>}
// // // //             </label>

// // // //             <div className="pm-sandbox-notice">
// // // //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// // // //             </div>

// // // //             <button
// // // //               className="pm-pay-btn"
// // // //               onClick={handleInitiate}
// // // //               disabled={loading}
// // // //             >
// // // //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// // // //             </button>

// // // //             <button className="pm-back-btn" onClick={onClose}>
// // // //               ← Change Plan
// // // //             </button>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// // // //         {step === 'waiting' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-spinner" />
// // // //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// // // //             <p className="pm-waiting-desc">
// // // //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// // // //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// // // //             </p>
// // // //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// // // //             <p className="pm-polling-hint">
// // // //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// // // //             </p>
// // // //             <button
// // // //               className="pm-back-btn"
// // // //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// // // //             >
// // // //               ← Cancel & Re-enter Number
// // // //             </button>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// // // //         {step === 'success' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-success-icon">✅</div>
// // // //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// // // //             <p className="pm-success-desc">
// // // //               Your <strong>{plan.planName}</strong> plan is now active.
// // // //             </p>
// // // //             {receipt && (
// // // //               <div className="pm-receipt">
// // // //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// // // //                 <span className="pm-receipt-code">{receipt}</span>
// // // //               </div>
// // // //             )}
// // // //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// // // //         {step === 'failed' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-fail-icon">❌</div>
// // // //             <h3 className="pm-fail-title">Payment Failed</h3>
// // // //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// // // //             <button
// // // //               className="pm-pay-btn"
// // // //               onClick={() => { setStep('phone'); setError(''); }}
// // // //             >
// // // //               Try Again
// // // //             </button>
// // // //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// // // //           </div>
// // // //         )}

// // // //       </div>
// // // //     </div>
// // // //   );
// // // // };

// // // // export default PaymentModal;
























// // // // import React, { useState, useEffect, useRef } from 'react';
// // // // import './PaymentModal.css';

// // // // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // // function getToken() {
// // // //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // // // }

// // // // /**
// // // //  * PaymentModal
// // // //  * Shows after user selects a paid plan.
// // // //  * Collects phone number → triggers STK Push → polls status every 3s.
// // // //  *
// // // //  * Props:
// // // //  *   isOpen       {boolean}
// // // //  *   plan         {object}  — { planId, planName, price, ... }
// // // //  *   adId         {number}  — the ad just created (pending activation)
// // // //  *   onSuccess    {fn}      — called when payment confirmed
// // // //  *   onClose      {fn}
// // // //  */
// // // // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// // // //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// // // //   const [phone,     setPhone]     = useState('');
// // // //   const [error,     setError]     = useState('');
// // // //   const [loading,   setLoading]   = useState(false);
// // // //   const [receipt,   setReceipt]   = useState('');
// // // //   const [checkoutId, setCheckoutId] = useState('');
// // // //   const [pollCount,  setPollCount]  = useState(0);
// // // //   const pollRef = useRef(null);

// // // //   // Reset when modal opens
// // // //   useEffect(() => {
// // // //     if (isOpen) {
// // // //       setStep('phone');
// // // //       setPhone('');
// // // //       setError('');
// // // //       setReceipt('');
// // // //       setCheckoutId('');
// // // //       setPollCount(0);
// // // //     }
// // // //     return () => clearInterval(pollRef.current);
// // // //   }, [isOpen]);

// // // //   if (!isOpen || !plan) return null;

// // // //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// // // //   const handleInitiate = async () => {
// // // //     const cleaned = phone.trim().replace(/\s|-/g, '');
// // // //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// // // //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// // // //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// // // //     }
// // // //     setError('');
// // // //     setLoading(true);

// // // //     try {
// // // //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// // // //         method:  'POST',
// // // //         headers: {
// // // //           'Authorization': `Token ${getToken()}`,
// // // //           'Content-Type':  'application/json',
// // // //         },
// // // //         body: JSON.stringify({
// // // //           phone:  cleaned,
// // // //           plan:   plan.planId,
// // // //           ad_id:  adId,
// // // //         }),
// // // //       });

// // // //       const data = await res.json();

// // // //       if (!res.ok) {
// // // //         setError(data.error || 'Failed to initiate payment. Try again.');
// // // //         setLoading(false);
// // // //         return;
// // // //       }

// // // //       setCheckoutId(data.checkout_request_id);
// // // //       setStep('waiting');
// // // //       setLoading(false);

// // // //       // Start polling every 3 seconds
// // // //       startPolling(data.checkout_request_id);

// // // //     } catch (e) {
// // // //       setError('Network error. Check your connection and try again.');
// // // //       setLoading(false);
// // // //     }
// // // //   };

// // // //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// // // //   const startPolling = (cid) => {
// // // //     clearInterval(pollRef.current);
// // // //     setPollCount(0);

// // // //     pollRef.current = setInterval(async () => {
// // // //       setPollCount(c => {
// // // //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// // // //           clearInterval(pollRef.current);
// // // //           setStep('failed');
// // // //           setError('Payment timed out. Please try again.');
// // // //           return c;
// // // //         }
// // // //         return c + 1;
// // // //       });

// // // //       try {
// // // //         const res = await fetch(
// // // //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// // // //           { headers: { 'Authorization': `Token ${getToken()}` } }
// // // //         );
// // // //         const data = await res.json();

// // // //         if (data.status === 'completed') {
// // // //           clearInterval(pollRef.current);
// // // //           setReceipt(data.mpesa_receipt || '');
// // // //           setStep('success');
// // // //           // Notify parent after short delay so user sees success screen
// // // //           setTimeout(() => onSuccess?.(data), 2000);
// // // //         } else if (data.status === 'failed') {
// // // //           clearInterval(pollRef.current);
// // // //           setStep('failed');
// // // //           setError(data.result_desc || 'Payment was cancelled or failed.');
// // // //         }
// // // //         // 'pending' → keep polling
// // // //       } catch { /* ignore poll errors */ }
// // // //     }, 3000);
// // // //   };

// // // //   // ── Amount display ────────────────────────────────────────────────────────
// // // //   const amountMap = { basic: 50, pro: 150, business: 500 };
// // // //   const amount    = amountMap[plan?.planId] || plan?.price || 0;

// // // //   return (
// // // //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// // // //       <div className="pm-modal">

// // // //         {/* Header */}
// // // //         <div className="pm-header">
// // // //           <div className="pm-header-left">
// // // //             <span className="pm-logo">M-PESA</span>
// // // //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// // // //           </div>
// // // //           {step === 'phone' && (
// // // //             <button className="pm-close" onClick={onClose}>✕</button>
// // // //           )}
// // // //         </div>

// // // //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// // // //         {step === 'phone' && (
// // // //           <div className="pm-body">
// // // //             <div className="pm-amount-display">
// // // //               <span className="pm-currency">KES</span>
// // // //               <span className="pm-amount">{amount.toLocaleString()}</span>
// // // //             </div>
// // // //             <p className="pm-description">
// // // //               You will receive an M-Pesa prompt on your phone to complete this payment.
// // // //             </p>

// // // //             <label className="pm-label">
// // // //               M-Pesa Phone Number
// // // //               <div className="pm-phone-wrap">
// // // //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// // // //                 <input
// // // //                   className="pm-phone-input"
// // // //                   type="tel"
// // // //                   placeholder="712 345 678"
// // // //                   value={phone}
// // // //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// // // //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// // // //                   autoFocus
// // // //                 />
// // // //               </div>
// // // //               {error && <span className="pm-error">{error}</span>}
// // // //             </label>

// // // //             <div className="pm-sandbox-notice">
// // // //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// // // //             </div>

// // // //             <button
// // // //               className="pm-pay-btn"
// // // //               onClick={handleInitiate}
// // // //               disabled={loading}
// // // //             >
// // // //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// // // //             </button>

// // // //             <button className="pm-back-btn" onClick={onClose}>
// // // //               ← Change Plan
// // // //             </button>

// // // //             {/* ✅ NEW: Bypass Payment button (temporary for testing) */}
// // // //             <button 
// // // //               className="pm-bypass-btn"
// // // //               onClick={() => {
// // // //                 // Call onSuccess without actual payment
// // // //                 // This simulates a successful payment
// // // //                 onSuccess?.({
// // // //                   status: 'completed',
// // // //                   mpesa_receipt: 'BYPASS-' + Date.now(),
// // // //                   bypass: true,
// // // //                 });
// // // //               }}
// // // //             >
// // // //               🚀 Bypass Payment (Dev Only)
// // // //             </button>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// // // //         {step === 'waiting' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-spinner" />
// // // //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// // // //             <p className="pm-waiting-desc">
// // // //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// // // //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// // // //             </p>
// // // //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// // // //             <p className="pm-polling-hint">
// // // //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// // // //             </p>
// // // //             <button
// // // //               className="pm-back-btn"
// // // //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// // // //             >
// // // //               ← Cancel & Re-enter Number
// // // //             </button>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// // // //         {step === 'success' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-success-icon">✅</div>
// // // //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// // // //             <p className="pm-success-desc">
// // // //               Your <strong>{plan.planName}</strong> plan is now active.
// // // //             </p>
// // // //             {receipt && (
// // // //               <div className="pm-receipt">
// // // //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// // // //                 <span className="pm-receipt-code">{receipt}</span>
// // // //               </div>
// // // //             )}
// // // //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// // // //           </div>
// // // //         )}

// // // //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// // // //         {step === 'failed' && (
// // // //           <div className="pm-body pm-center">
// // // //             <div className="pm-fail-icon">❌</div>
// // // //             <h3 className="pm-fail-title">Payment Failed</h3>
// // // //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// // // //             <button
// // // //               className="pm-pay-btn"
// // // //               onClick={() => { setStep('phone'); setError(''); }}
// // // //             >
// // // //               Try Again
// // // //             </button>
// // // //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// // // //           </div>
// // // //         )}

// // // //       </div>
// // // //     </div>
// // // //   );
// // // // };

// // // // export default PaymentModal;





























// // // // // import React, { useState, useEffect, useRef } from 'react';
// // // // // import './PaymentModal.css';

// // // // // const API_BASE = 'https://imselling-marketplace-production.up.railway.app/api';

// // // // // function getToken() {
// // // // //   return localStorage.getItem('authToken') || localStorage.getItem('token') || '';
// // // // // }

// // // // // /**
// // // // //  * PaymentModal
// // // // //  * Shows after user selects a paid plan.
// // // // //  * Collects phone number → triggers STK Push → polls status every 3s.
// // // // //  *
// // // // //  * Props:
// // // // //  *   isOpen       {boolean}
// // // // //  *   plan         {object}  — { planId, planName, price, ... }
// // // // //  *   adId         {number}  — the ad just created (pending activation)
// // // // //  *   onSuccess    {fn}      — called when payment confirmed
// // // // //  *   onClose      {fn}
// // // // //  */
// // // // // const PaymentModal = ({ isOpen, plan, adId, onSuccess, onClose }) => {
// // // // //   const [step,      setStep]      = useState('phone');   // 'phone' | 'waiting' | 'success' | 'failed'
// // // // //   const [phone,     setPhone]     = useState('');
// // // // //   const [error,     setError]     = useState('');
// // // // //   const [loading,   setLoading]   = useState(false);
// // // // //   const [receipt,   setReceipt]   = useState('');
// // // // //   const [checkoutId, setCheckoutId] = useState('');
// // // // //   const [pollCount,  setPollCount]  = useState(0);
// // // // //   const pollRef = useRef(null);

// // // // //   // Reset when modal opens
// // // // //   useEffect(() => {
// // // // //     if (isOpen) {
// // // // //       setStep('phone');
// // // // //       setPhone('');
// // // // //       setError('');
// // // // //       setReceipt('');
// // // // //       setCheckoutId('');
// // // // //       setPollCount(0);
// // // // //     }
// // // // //     return () => clearInterval(pollRef.current);
// // // // //   }, [isOpen]);

// // // // //   if (!isOpen || !plan) return null;

// // // // //   // ── Step 1: Initiate STK Push ─────────────────────────────────────────────
// // // // //   const handleInitiate = async () => {
// // // // //     const cleaned = phone.trim().replace(/\s|-/g, '');
// // // // //     if (!cleaned) { setError('Please enter your phone number.'); return; }
// // // // //     if (!/^(07|01|2547|2541|\+2547|\+2541)/.test(cleaned)) {
// // // // //       setError('Enter a valid Kenyan number e.g. 0712345678'); return;
// // // // //     }
// // // // //     setError('');
// // // // //     setLoading(true);

// // // // //     try {
// // // // //       const res = await fetch(`${API_BASE}/mpesa/initiate/`, {
// // // // //         method:  'POST',
// // // // //         headers: {
// // // // //           'Authorization': `Token ${getToken()}`,
// // // // //           'Content-Type':  'application/json',
// // // // //         },
// // // // //         body: JSON.stringify({
// // // // //           phone:  cleaned,
// // // // //           plan:   plan.planId,
// // // // //           ad_id:  adId,
// // // // //         }),
// // // // //       });

// // // // //       const data = await res.json();

// // // // //       if (!res.ok) {
// // // // //         setError(data.error || 'Failed to initiate payment. Try again.');
// // // // //         setLoading(false);
// // // // //         return;
// // // // //       }

// // // // //       setCheckoutId(data.checkout_request_id);
// // // // //       setStep('waiting');
// // // // //       setLoading(false);

// // // // //       // Start polling every 3 seconds
// // // // //       startPolling(data.checkout_request_id);

// // // // //     } catch (e) {
// // // // //       setError('Network error. Check your connection and try again.');
// // // // //       setLoading(false);
// // // // //     }
// // // // //   };

// // // // //   // ── Step 2: Poll payment status ───────────────────────────────────────────
// // // // //   const startPolling = (cid) => {
// // // // //     clearInterval(pollRef.current);
// // // // //     setPollCount(0);

// // // // //     pollRef.current = setInterval(async () => {
// // // // //       setPollCount(c => {
// // // // //         if (c >= 40) {           // 40 × 3s = 2 minutes timeout
// // // // //           clearInterval(pollRef.current);
// // // // //           setStep('failed');
// // // // //           setError('Payment timed out. Please try again.');
// // // // //           return c;
// // // // //         }
// // // // //         return c + 1;
// // // // //       });

// // // // //       try {
// // // // //         const res = await fetch(
// // // // //           `${API_BASE}/mpesa/status/?checkout_request_id=${encodeURIComponent(cid)}`,
// // // // //           { headers: { 'Authorization': `Token ${getToken()}` } }
// // // // //         );
// // // // //         const data = await res.json();

// // // // //         if (data.status === 'completed') {
// // // // //           clearInterval(pollRef.current);
// // // // //           setReceipt(data.mpesa_receipt || '');
// // // // //           setStep('success');
// // // // //           // Notify parent after short delay so user sees success screen
// // // // //           setTimeout(() => onSuccess?.(data), 2000);
// // // // //         } else if (data.status === 'failed') {
// // // // //           clearInterval(pollRef.current);
// // // // //           setStep('failed');
// // // // //           setError(data.result_desc || 'Payment was cancelled or failed.');
// // // // //         }
// // // // //         // 'pending' → keep polling
// // // // //       } catch { /* ignore poll errors */ }
// // // // //     }, 3000);
// // // // //   };

// // // // //   // ── Amount display ────────────────────────────────────────────────────────
// // // // //   const amountMap = { basic: 50, pro: 150, business: 500 };
// // // // //   const amount    = amountMap[plan?.planId] || plan?.price || 0;

// // // // //   return (
// // // // //     <div className="pm-overlay" onClick={e => { if (e.target === e.currentTarget) onClose(); }}>
// // // // //       <div className="pm-modal">

// // // // //         {/* Header */}
// // // // //         <div className="pm-header">
// // // // //           <div className="pm-header-left">
// // // // //             <span className="pm-logo">M-PESA</span>
// // // // //             <span className="pm-plan-badge">{plan.planName} Plan</span>
// // // // //           </div>
// // // // //           {step === 'phone' && (
// // // // //             <button className="pm-close" onClick={onClose}>✕</button>
// // // // //           )}
// // // // //         </div>

// // // // //         {/* ── STEP: Phone input ─────────────────────────────────────────── */}
// // // // //         {step === 'phone' && (
// // // // //           <div className="pm-body">
// // // // //             <div className="pm-amount-display">
// // // // //               <span className="pm-currency">KES</span>
// // // // //               <span className="pm-amount">{amount.toLocaleString()}</span>
// // // // //             </div>
// // // // //             <p className="pm-description">
// // // // //               You will receive an M-Pesa prompt on your phone to complete this payment.
// // // // //             </p>

// // // // //             <label className="pm-label">
// // // // //               M-Pesa Phone Number
// // // // //               <div className="pm-phone-wrap">
// // // // //                 <span className="pm-phone-prefix">🇰🇪 +254</span>
// // // // //                 <input
// // // // //                   className="pm-phone-input"
// // // // //                   type="tel"
// // // // //                   placeholder="712 345 678"
// // // // //                   value={phone}
// // // // //                   onChange={e => { setPhone(e.target.value); setError(''); }}
// // // // //                   onKeyDown={e => e.key === 'Enter' && handleInitiate()}
// // // // //                   autoFocus
// // // // //                 />
// // // // //               </div>
// // // // //               {error && <span className="pm-error">{error}</span>}
// // // // //             </label>

// // // // //             <div className="pm-sandbox-notice">
// // // // //               🧪 <strong>Sandbox Mode</strong> — Use test number <strong>254708374149</strong> with PIN <strong>1234</strong>
// // // // //             </div>

// // // // //             <button
// // // // //               className="pm-pay-btn"
// // // // //               onClick={handleInitiate}
// // // // //               disabled={loading}
// // // // //             >
// // // // //               {loading ? '⏳ Sending prompt…' : `Pay KES ${amount.toLocaleString()} via M-Pesa`}
// // // // //             </button>

// // // // //             <button className="pm-back-btn" onClick={onClose}>
// // // // //               ← Change Plan
// // // // //             </button>
// // // // //           </div>
// // // // //         )}

// // // // //         {/* ── STEP: Waiting for payment ─────────────────────────────────── */}
// // // // //         {step === 'waiting' && (
// // // // //           <div className="pm-body pm-center">
// // // // //             <div className="pm-spinner" />
// // // // //             <h3 className="pm-waiting-title">Check Your Phone</h3>
// // // // //             <p className="pm-waiting-desc">
// // // // //               An M-Pesa payment prompt has been sent to <strong>{phone}</strong>.
// // // // //               Enter your <strong>M-Pesa PIN</strong> to complete the payment.
// // // // //             </p>
// // // // //             <div className="pm-amount-pill">KES {amount.toLocaleString()}</div>
// // // // //             <p className="pm-polling-hint">
// // // // //               Waiting for confirmation… ({Math.min(pollCount * 3, 120)}s)
// // // // //             </p>
// // // // //             <button
// // // // //               className="pm-back-btn"
// // // // //               onClick={() => { clearInterval(pollRef.current); setStep('phone'); }}
// // // // //             >
// // // // //               ← Cancel & Re-enter Number
// // // // //             </button>
// // // // //           </div>
// // // // //         )}

// // // // //         {/* ── STEP: Success ─────────────────────────────────────────────── */}
// // // // //         {step === 'success' && (
// // // // //           <div className="pm-body pm-center">
// // // // //             <div className="pm-success-icon">✅</div>
// // // // //             <h3 className="pm-success-title">Payment Confirmed!</h3>
// // // // //             <p className="pm-success-desc">
// // // // //               Your <strong>{plan.planName}</strong> plan is now active.
// // // // //             </p>
// // // // //             {receipt && (
// // // // //               <div className="pm-receipt">
// // // // //                 <span className="pm-receipt-label">M-Pesa Receipt</span>
// // // // //                 <span className="pm-receipt-code">{receipt}</span>
// // // // //               </div>
// // // // //             )}
// // // // //             <p className="pm-success-hint">Redirecting you to post your ad…</p>
// // // // //           </div>
// // // // //         )}

// // // // //         {/* ── STEP: Failed ──────────────────────────────────────────────── */}
// // // // //         {step === 'failed' && (
// // // // //           <div className="pm-body pm-center">
// // // // //             <div className="pm-fail-icon">❌</div>
// // // // //             <h3 className="pm-fail-title">Payment Failed</h3>
// // // // //             <p className="pm-fail-desc">{error || 'Payment was not completed.'}</p>
// // // // //             <button
// // // // //               className="pm-pay-btn"
// // // // //               onClick={() => { setStep('phone'); setError(''); }}
// // // // //             >
// // // // //               Try Again
// // // // //             </button>
// // // // //             <button className="pm-back-btn" onClick={onClose}>← Change Plan</button>
// // // // //           </div>
// // // // //         )}

// // // // //       </div>
// // // // //     </div>
// // // // //   );
// // // // // };

// // // // // export default PaymentModal;

